import React, { ReactNode, useContext, useEffect, useRef, useState } from "react";
import { MGrid } from "../Shared"
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import { InspectorServiceRegionResponse, ServiceRegionSlim } from "interfaces/Mueller.Business.Models";
import { getInspectorServiceRegions } from "requests/inspectorServiceRegions";
import { MGridColumnDefinition } from "components/Shared/MGrid";
import { isValidMsa } from ".";

interface Props {
  inspectorNumberForViewOnly?: number, // supply this if you want the control in view only mode, it will load its own data
  // has to be a ref to the function because the button wont re render every time this is updated, causing stale referenced functions to be called
  onDeleteRef?: React.MutableRefObject<(row: InspectorServiceRegionResponse) => void>,
  selectedServiceRegions?: InspectorServiceRegionResponse[],
  isLoading?: boolean,
  isEditingMSAs?: boolean,
  availableMSAList?: ServiceRegionSlim[],

  selectedMSAs?: number[], // array of row index
  onSelectedMSAsChanged?: (selectedMSAs: number[]) => void,
};

const getRegionType = (region: InspectorServiceRegionResponse) =>
  region.countyId && "County" ||
  region.postalId && "Zip" ||
  region.serviceRegionId && "MSA";

const RegionManagementPane = ({
  inspectorNumberForViewOnly,
  onDeleteRef,
  selectedServiceRegions,
  isLoading,
  isEditingMSAs,
  availableMSAList,
  selectedMSAs,
  onSelectedMSAsChanged,
}: Props) => {
  const mounted = useRef(true);

  // we will load our own data if it is not passed in, this control is meant to be used as a view and edit control
  const [viewServiceRegions, setViewServiceRegions] = useState<InspectorServiceRegionResponse[]>();

  useEffect(() => {
    if (inspectorNumberForViewOnly) {
      getInspectorServiceRegions(inspectorNumberForViewOnly, (serviceRegions) => {
        // if it doesnt have a state linked to it, it is an empty/dead region and we dont want to display it
        // dont show regions linked to travel rules
        setViewServiceRegions(serviceRegions.filter(sr => !!sr.stateAbbreviation && !sr.travelRuleId));
      }, mounted);
    }
  }, [inspectorNumberForViewOnly]);

  let serviceRegionsToDisplay = selectedServiceRegions ?? viewServiceRegions;

  let listPane: ReactNode;

  if (isEditingMSAs) {

    listPane = (
      <div className="add-msa-pane">
        <MGrid
          columnDefinitions={[
            { key: "areaDescription", name: "Name", dataSource: "name" },
          ]}
          data={availableMSAList}
          allowRowSelection
          selectedRows={selectedMSAs}
          onRowSelectionChanged={onSelectedMSAsChanged}
        />
      </div>
    );
  } else {
    let coldefs: MGridColumnDefinition[] = [
      { key: "areaType", name: "Type", width: 75, dataSource: "regionType" },
      { key: "areaDescription", name: "Name", dataSource: "name" },
    ];

    if (!inspectorNumberForViewOnly) {
      coldefs.push({
        key: "delete",
        name: "Remove",
        width: 70,
        dataSource: (row) => (
          <IconButton
            onClick={() => onDeleteRef.current(row)}
            title="Delete"
          ><Icon>delete</Icon></IconButton>)
      });
    }

    listPane = (
      <div className="region-list-pane">
        <MGrid
          disableFilters
          columnDefinitions={coldefs}
          isFetchingData={!serviceRegionsToDisplay || isLoading}
          data={serviceRegionsToDisplay?.map(r => ({ ...r, regionType: getRegionType(r) }))}
        />
      </div>
    );
  }

  return listPane;
};

export default RegionManagementPane;
